import { useState, useEffect, useRef, useCallback } from 'react';
import { 
  X, Folder, File, Download, Upload, Trash2, RefreshCw, 
  FolderPlus, ChevronUp, Save, Eye, AlertCircle 
} from 'lucide-react';
import { getResponseErrorMessage } from '../utils/apiError';

interface FileItem {
  name: string;
  type: 'directory' | 'file';
  size: number | null;
  modified: string;
  permissions?: string;
  path: string;
}

interface SecureFileManagerProps {
  serverId: string;
  serverName: string;
  onClose?: () => void;
}

/**
 * Secure File Manager that communicates through Mothership's REST API.
 * This ensures proper authentication and authorization for file operations.
 */
export function SecureFileManager({ serverId, serverName, onClose }: SecureFileManagerProps) {
  const [currentPath, setCurrentPath] = useState<string>('/');
  const [files, setFiles] = useState<FileItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<Set<string>>(new Set());
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [showCreateFolder, setShowCreateFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [showTextEditor, setShowTextEditor] = useState(false);
  const [editingFile, setEditingFile] = useState<{ path: string; content: string; name: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const [storage, setStorage] = useState<{ used: number; limit: number | null } | null>(null);
  
  const fileInputRef = useRef<HTMLInputElement>(null);

  /** Returns auth headers for every API call */
  const getAuthHeaders = (): HeadersInit => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchFiles = useCallback(async (path: string) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(
        `/api/files/${serverId}/files?path=${encodeURIComponent(path)}`,
        { headers: getAuthHeaders() }
      );
      
      if (!response.ok) {
        throw new Error(await getResponseErrorMessage(response, 'Failed to load files'));
      }
      
      const data = await response.json();
      setFiles(data.files || []);
      setCurrentPath(data.path || path);
      const storageResponse = await fetch(`/api/files/${serverId}/storage`, { headers: getAuthHeaders() });
      if (storageResponse.ok) {
        const storageData = await storageResponse.json();
        if (Number.isSafeInteger(storageData.used) && storageData.used >= 0) {
          setStorage({
            used: storageData.used,
            limit: Number.isSafeInteger(storageData.limit) && storageData.limit > 0 ? storageData.limit : null,
          });
        }
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load files');
      setFiles([]);
    } finally {
      setLoading(false);
    }
  }, [serverId]);

  useEffect(() => {
    fetchFiles('/');
  }, [fetchFiles]);

  const navigateToPath = (path: string) => {
    setSelectedFiles(new Set());
    fetchFiles(path);
  };

  const navigateUp = () => {
    if (currentPath === '/' || currentPath === '.') return;
    const parentPath = currentPath.split('/').slice(0, -1).join('/') || '/';
    navigateToPath(parentPath);
  };

  const handleFileClick = (file: FileItem) => {
    if (file.type === 'directory') {
      const newPath = currentPath === '/' 
        ? `/${file.name}` 
        : `${currentPath}/${file.name}`;
      navigateToPath(newPath);
    }
  };

  const toggleFileSelection = (fileName: string) => {
    setSelectedFiles(prev => {
      const newSet = new Set(prev);
      if (newSet.has(fileName)) {
        newSet.delete(fileName);
      } else {
        newSet.add(fileName);
      }
      return newSet;
    });
  };

  const handleDownload = async (fileName: string) => {
    const filePath = currentPath === '/' ? `/${fileName}` : `${currentPath}/${fileName}`;
    
    try {
      const response = await fetch(
        `/api/files/${serverId}/download?path=${encodeURIComponent(filePath)}`,
        { headers: getAuthHeaders() }
      );
      
      if (!response.ok) throw new Error(await getResponseErrorMessage(response, 'Download failed'));
      
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err.message || 'Download failed');
    }
  };

  const handleDelete = async (fileName: string) => {
    if (!confirm(`Delete "${fileName}"?`)) return;
    
    const filePath = currentPath === '/' ? `/${fileName}` : `${currentPath}/${fileName}`;
    
    try {
      const response = await fetch(
        `/api/files/${serverId}/files?path=${encodeURIComponent(filePath)}`,
        { method: 'DELETE', headers: getAuthHeaders() }
      );
      
      if (!response.ok) {
        throw new Error(await getResponseErrorMessage(response, 'Delete failed'));
      }
      
      await fetchFiles(currentPath);
    } catch (err: any) {
      setError(err.message || 'Delete failed');
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedFiles.size === 0) return;
    if (!confirm(`Delete ${selectedFiles.size} selected item(s)?`)) return;
    
    setLoading(true);
    try {
      for (const fileName of selectedFiles) {
        const filePath = currentPath === '/' ? `/${fileName}` : `${currentPath}/${fileName}`;
        const response = await fetch(
          `/api/files/${serverId}/files?path=${encodeURIComponent(filePath)}`,
          { method: 'DELETE', headers: getAuthHeaders() }
        );
        if (!response.ok) {
          throw new Error(await getResponseErrorMessage(response, `Failed to delete ${fileName}`));
        }
      }
      setSelectedFiles(new Set());
      await fetchFiles(currentPath);
    } catch (err: any) {
      setError(err.message || 'Delete failed');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    
    try {
      const response = await fetch(`/api/files/${serverId}/mkdir`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ path: currentPath, name: newFolderName.trim() })
      });
      
      if (!response.ok) {
        throw new Error(await getResponseErrorMessage(response, 'Failed to create folder'));
      }
      
      setShowCreateFolder(false);
      setNewFolderName('');
      await fetchFiles(currentPath);
    } catch (err: any) {
      setError(err.message || 'Failed to create folder');
    }
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const uploadFiles = e.target.files;
    if (!uploadFiles || uploadFiles.length === 0) return;
    
    setUploadProgress(0);
    
    try {
      for (let i = 0; i < uploadFiles.length; i++) {
        const file = uploadFiles[i];
        const formData = new FormData();
        formData.append('files', file);
        formData.append('path', currentPath);
        
        const response = await fetch(`/api/files/${serverId}/upload`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: formData
        });
        
        if (!response.ok) {
          throw new Error(await getResponseErrorMessage(response, `Failed to upload ${file.name}`));
        }
        
        setUploadProgress(Math.round(((i + 1) / uploadFiles.length) * 100));
      }
      
      await fetchFiles(currentPath);
    } catch (err: any) {
      setError(err.message || 'Upload failed');
    } finally {
      setUploadProgress(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleEditFile = async (fileName: string) => {
    const filePath = currentPath === '/' ? `/${fileName}` : `${currentPath}/${fileName}`;
    
    setLoading(true);
    try {
      const response = await fetch(
        `/api/files/${serverId}/read?path=${encodeURIComponent(filePath)}`,
        { headers: getAuthHeaders() }
      );
      
      if (!response.ok) {
        throw new Error(await getResponseErrorMessage(response, 'Failed to read file'));
      }
      
      const data = await response.json();
      setEditingFile({ path: filePath, content: data.content || '', name: fileName });
      setShowTextEditor(true);
    } catch (err: any) {
      setError(err.message || 'Failed to read file');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveFile = async () => {
    if (!editingFile) return;
    
    setSaving(true);
    try {
      const response = await fetch(`/api/files/${serverId}/write`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ path: editingFile.path, content: editingFile.content })
      });
      
      if (!response.ok) {
        throw new Error(await getResponseErrorMessage(response, 'Failed to save file'));
      }
      
      setShowTextEditor(false);
      setEditingFile(null);
      await fetchFiles(currentPath);
    } catch (err: any) {
      setError(err.message || 'Failed to save file');
    } finally {
      setSaving(false);
    }
  };

  const formatFileSize = (bytes: number | null) => {
    if (bytes === null || bytes === 0) return '-';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const isTextFile = (fileName: string) => {
    const textExtensions = [
      '.txt', '.json', '.yml', '.yaml', '.properties', '.cfg', '.conf', '.config',
      '.ini', '.log', '.md', '.xml', '.html', '.css', '.js', '.ts', '.sh', '.bat',
      '.toml', '.env', '.htaccess', '.gitignore'
    ];
    return textExtensions.some(ext => fileName.toLowerCase().endsWith(ext));
  };

  const pathSegments = currentPath === '/' ? [] : currentPath.split('/').filter(Boolean);

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-dark-800 rounded-lg w-full max-w-5xl h-[80vh] flex flex-col border border-dark-600 shadow-xl">
        {/* Header */}
        <div className="flex justify-between items-center px-4 py-3 border-b border-dark-600">
          <div className="flex items-center gap-3">
            <Folder className="w-5 h-5 text-neon-400" />
            <h2 className="font-semibold text-gray-100">File Manager - {serverName}</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Toolbar */}
        <div className="flex items-center gap-2 px-4 py-2 border-b border-dark-700 bg-dark-900/50">
          <button
            onClick={navigateUp}
            disabled={currentPath === '/' || currentPath === '.'}
            className="p-2 rounded hover:bg-dark-700 disabled:opacity-50 disabled:cursor-not-allowed"
            title="Go up"
          >
            <ChevronUp className="w-4 h-4" />
          </button>
          
          <button
            onClick={() => fetchFiles(currentPath)}
            disabled={loading}
            className="p-2 rounded hover:bg-dark-700 disabled:opacity-50"
            title="Refresh"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>

          <div className="w-px h-6 bg-dark-600 mx-1" />

          <button
            onClick={() => setShowCreateFolder(true)}
            className="p-2 rounded hover:bg-dark-700"
            title="New folder"
          >
            <FolderPlus className="w-4 h-4" />
          </button>

          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploadProgress !== null}
            className="p-2 rounded hover:bg-dark-700 disabled:opacity-50"
            title="Upload"
          >
            <Upload className="w-4 h-4" />
          </button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={handleUpload}
          />

          {selectedFiles.size > 0 && (
            <>
              <div className="w-px h-6 bg-dark-600 mx-1" />
              <button
                onClick={handleDeleteSelected}
                className="p-2 rounded hover:bg-red-900/50 text-red-400"
                title={`Delete ${selectedFiles.size} selected`}
              >
                <Trash2 className="w-4 h-4" />
              </button>
              <span className="text-xs text-gray-500">{selectedFiles.size} selected</span>
            </>
          )}

          {uploadProgress !== null && (
            <div className="ml-auto flex items-center gap-2 text-sm text-gray-400">
              <span>Uploading...</span>
              <div className="w-24 h-2 bg-dark-700 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-neon-500 transition-all duration-200"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
              <span>{uploadProgress}%</span>
            </div>
          )}

          {storage && (
            <span className="ml-auto text-xs text-gray-500">
              Storage: {formatFileSize(storage.used)}
              {storage.limit ? ` / ${formatFileSize(storage.limit)}` : ''}
            </span>
          )}
        </div>

        {/* Breadcrumb */}
        <div className="flex items-center gap-1 px-4 py-2 text-sm text-gray-400 bg-dark-900/30">
          <button
            onClick={() => navigateToPath('/')}
            className="hover:text-white transition-colors"
          >
            /
          </button>
          {pathSegments.map((segment, index) => (
            <span key={index} className="flex items-center gap-1">
              <span>/</span>
              <button
                onClick={() => navigateToPath('/' + pathSegments.slice(0, index + 1).join('/'))}
                className="hover:text-white transition-colors"
              >
                {segment}
              </button>
            </span>
          ))}
        </div>

        {/* Error Alert */}
        {error && (
          <div className="mx-4 mt-2 p-3 bg-red-900/30 border border-red-800 rounded-lg flex items-center gap-2 text-red-300 text-sm">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <span>{error}</span>
            <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-200">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* File List */}
        <div className="flex-1 overflow-auto">
          {loading && files.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <div className="spinner w-8 h-8" />
            </div>
          ) : files.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-500">
              <Folder className="w-12 h-12 mb-2 opacity-50" />
              <p>This folder is empty</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-dark-800 text-gray-400 text-left">
                <tr>
                  <th className="w-8 px-4 py-2"></th>
                  <th className="px-4 py-2">Name</th>
                  <th className="px-4 py-2 w-24">Size</th>
                  <th className="px-4 py-2 w-48">Modified</th>
                  <th className="px-4 py-2 w-32">Actions</th>
                </tr>
              </thead>
              <tbody>
                {[...files]
                  .sort((a, b) => {
                    if (a.type !== b.type) return a.type === 'directory' ? -1 : 1;
                    return a.name.localeCompare(b.name);
                  })
                  .map((file) => (
                    <tr
                      key={file.name}
                      className={`border-b border-dark-700 hover:bg-dark-700/50 transition-colors ${
                        selectedFiles.has(file.name) ? 'bg-dark-700/70' : ''
                      }`}
                    >
                      <td className="px-4 py-2">
                        <input
                          type="checkbox"
                          checked={selectedFiles.has(file.name)}
                          onChange={() => toggleFileSelection(file.name)}
                          className="w-4 h-4 rounded border-dark-600 text-neon-500 focus:ring-neon-500"
                        />
                      </td>
                      <td 
                        className="px-4 py-2 cursor-pointer"
                        onClick={() => handleFileClick(file)}
                      >
                        <div className="flex items-center gap-2">
                          {file.type === 'directory' ? (
                            <Folder className="w-4 h-4 text-yellow-500" />
                          ) : (
                            <File className="w-4 h-4 text-gray-400" />
                          )}
                          <span className="text-gray-200 hover:text-white transition-colors">
                            {file.name}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-2 text-gray-500">
                        {file.type === 'file' ? formatFileSize(file.size) : '-'}
                      </td>
                      <td className="px-4 py-2 text-gray-500">
                        {file.modified ? new Date(file.modified).toLocaleString() : '-'}
                      </td>
                      <td className="px-4 py-2">
                        <div className="flex items-center gap-1">
                          {file.type === 'file' && (
                            <>
                              {isTextFile(file.name) && (
                                <button
                                  onClick={() => handleEditFile(file.name)}
                                  className="p-1.5 rounded hover:bg-dark-600 text-gray-400 hover:text-white"
                                  title="Edit"
                                >
                                  <Eye className="w-4 h-4" />
                                </button>
                              )}
                              <button
                                onClick={() => handleDownload(file.name)}
                                className="p-1.5 rounded hover:bg-dark-600 text-gray-400 hover:text-white"
                                title="Download"
                              >
                                <Download className="w-4 h-4" />
                              </button>
                            </>
                          )}
                          <button
                            onClick={() => handleDelete(file.name)}
                            className="p-1.5 rounded hover:bg-red-900/50 text-gray-400 hover:text-red-400"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Create Folder Modal */}
        {showCreateFolder && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
            <div className="bg-dark-800 rounded-lg p-4 w-80 border border-dark-600">
              <h3 className="font-medium mb-3">New Folder</h3>
              <input
                type="text"
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleCreateFolder()}
                placeholder="Folder name"
                className="input-field w-full mb-3"
                autoFocus
              />
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => { setShowCreateFolder(false); setNewFolderName(''); }}
                  className="btn-secondary text-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateFolder}
                  disabled={!newFolderName.trim()}
                  className="btn-primary text-sm"
                >
                  Create
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Text Editor Modal */}
        {showTextEditor && editingFile && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center p-4">
            <div className="bg-dark-800 rounded-lg w-full max-w-4xl h-[70vh] flex flex-col border border-dark-600">
              <div className="flex justify-between items-center px-4 py-3 border-b border-dark-600">
                <h3 className="font-medium">{editingFile.name}</h3>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleSaveFile}
                    disabled={saving}
                    className="btn-primary text-sm flex items-center gap-1"
                  >
                    <Save className="w-4 h-4" />
                    {saving ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    onClick={() => { setShowTextEditor(false); setEditingFile(null); }}
                    className="text-gray-400 hover:text-white"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
              <textarea
                value={editingFile.content}
                onChange={(e) => setEditingFile({ ...editingFile, content: e.target.value })}
                className="flex-1 bg-dark-900 text-gray-200 font-mono text-sm p-4 resize-none focus:outline-none"
                spellCheck={false}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
